import { mdiUpload } from "@mdi/js";
import Icon from "@mdi/react";
import { useState } from "react";
import { FunctionComponent } from "react";
import { useOrderCreateCtx } from "../../../../../context_providers/order_create_context";
import ExtraRowWidget from "./extra_row";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { fetchGet, toQueryString } from "../../../../../service/api_client";
import ExtraItemBulkUpload from "./ExtraItemBulkUpload";

interface ExtraSectionWidgetProps {}
interface BulkUploadResult {
  successCount: number;
  failedCount: number;
  successRows: any[];
  failedRows: any[];
}

const ConsumptionSectionWidget: FunctionComponent<
  ExtraSectionWidgetProps
> = () => {
  const { order, setOrder } = useOrderCreateCtx();
  const [formId, setFormId] = useState(0);
  const { showToast } = useToast();
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const validateAndFetchData = async (
    warehouse_id: string,
    item_code: string,
    qty: number,
  ): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const warehouseUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/warehouse_type`;
      const warehouseRes = await fetchGet(
        warehouseUrl +
          toQueryString({
            search: warehouse_id,
            page: 1,
            count: 1,
            all: true,
          }),
      );

      if (!warehouseRes.success || !warehouseRes.data?.data?.length) {
        return {
          success: false,
          error: `Warehouse '${warehouse_id}' not found`,
        };
      }

      const warehouse = warehouseRes.data.data.find(
        (w: any) => w.id === warehouse_id || w.name === warehouse_id,
      );

      if (!warehouse) {
        return {
          success: false,
          error: `Warehouse '${warehouse_id}' not found`,
        };
      }

      // Validate and fetch item
      const itemUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/orders/get-available-consumables-by-item-code`;
      const itemRes = await fetchGet(
        itemUrl +
          toQueryString({
            warehouse_id: warehouse.id,
            item_code: item_code,
          }),
      );

      if (!itemRes.success || !itemRes.data?.length) {
        return {
          success: false,
          error: `Item '${item_code}' not found in warehouse '${warehouse_id}'`,
        };
      }

      const item = itemRes.data.find(
        (i: any) => i.item_code === item_code || i.name === item_code,
      );

      if (!item) {
        return {
          success: false,
          error: `Item '${item_code}' not found`,
        };
      }

      // Validate quantity against available quantity
      const availableQty = item.available_units ?? 0;
      if (qty > availableQty) {
        return {
          success: false,
          error: `Requested qty ${qty} exceeds available qty ${availableQty}`,
        };
      }

      // Return populated data
      return {
        success: true,
        data: {
          id: Date.now() + Math.random(),
          warehouse_id: warehouse.id,
          warehouse: warehouse,
          item_id: item.id,
          item: item,
          qty: qty,
          available_qty: availableQty,
          price: item.price || 0,
          delivered_qty: 0,
          installed_qty: 0,
          returned_qty: 0,
        },
      };
    } catch (error: any) {
      console.error("Validation error:", error);
      return {
        success: false,
        error: error.message || "Failed to validate data",
      };
    }
  };

  const handleBulkUploadComplete = async (results: BulkUploadResult) => {
    setIsProcessing(true);

    try {
      const newItems: any = [];

      // Process each successful row
      for (const successRow of results.successRows) {
        const result = await validateAndFetchData(
          successRow.warehouse_id,
          successRow.item_code,
          successRow.qty,
        );

        if (result.success && result.data) {
          newItems.push(result.data);
        }
      }

      if (newItems.length > 0) {
        // Add all new items to the order
        setOrder((old) => ({
          ...old,
          excessData: [...old.excessData, ...newItems],
        }));

        showToast({
          type: ToastType.success,
          text: `Successfully added ${newItems.length} items`,
        });
      }

      if (results.failedCount > 0) {
        showToast({
          type: ToastType.warning,
          text: `${results.failedCount} items failed validation`,
        });
      }
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to process bulk upload",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div id="order-create-consumables-section" className="overflow-x-auto">
        <div className="min-w-0">
          <div className="bg-white rounded-md shadow p-3 my-2 text-sm">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-lg">Consumables</h3>
              <button
                id="order-create-consumables-bulk-upload-btn"
                onClick={() => setShowBulkUpload(true)}
                className="flex items-center gap-2 bg-myPrimaryColor text-white px-4 py-2 rounded-lg  transition-colors shadow-md"
              >
                <Icon path={mdiUpload} className="h-5 w-5" />
                Bulk Upload CSV
              </button>
            </div>

            <div className="hidden md:grid grid-cols-7 gap-3 ">
              <h4 className="font-semibold">
                <span className="text-base text-red-500">*</span>Warehouse
              </h4>
              <h4 className="font-semibold">
                <span className="text-base text-red-500">*</span>Item Code
              </h4>
              <h4 className="font-semibold">
                <span className="text-base text-red-500">*</span>Item
              </h4>
              <h4 className="font-semibold">
                <span className="text-base text-red-500">*</span>Item
                Description
              </h4>

              <h4 className="font-semibold">
                <span className="text-base text-red-500">*</span>Item Unit
              </h4>
              <h4 className="font-semibold">
                <span className="text-base text-red-500">*</span>qty
              </h4>
              <h4 className="font-semibold text-center">
                <span className="text-base text-red-500">*</span>Actions
              </h4>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {order.excessData.map((extra, i) => (
              <ExtraRowWidget
                id_prefix="order-create-consumable"
                edit={false}
                key={extra.id}
                extra={extra}
                onDelete={(it) => {
                  const excessData = [...order.excessData].filter(
                    (v, j) => v.id !== it.id,
                  );
                  // const excess = GetExcessAmt(excessData);
                  setOrder((old) => ({
                    ...old,
                    // excess,
                    excessData,
                  }));
                }}
                onSubmit={(data) => {
                  const excessData = order.excessData.map((it, j) => {
                    if (it.id === extra.id) {
                      return data;
                    }
                    return it;
                  });
                  // const excess = GetExcessAmt(excessData);
                  setOrder((old) => ({
                    ...old,
                    // excess,
                    excessData,
                  }));
                }}
              />
            ))}
            <ExtraRowWidget
              key={formId}
              id_prefix="order-create-consumable"
              edit={true}
              // form_id="extra_form"
              onSubmit={(data) => {
                data.id = Date.now();
                const excessData = [...order.excessData, data];
                // const excess = GetExcessAmt(excessData);
                setOrder((old) => ({
                  ...old,
                  // excess,
                  excessData,
                }));
                setFormId(Math.random());
              }}
            />
            <div className="flex justify-between">
              <p className="text-sm italic text-gray-400">
                Please select category,description and amount then hit + to add
                extra.
              </p>
            </div>
          </div>
        </div>
      </div>
      {showBulkUpload && (
        <ExtraItemBulkUpload
          id_prefix="order-create-consumables-bulk"
          onUploadComplete={handleBulkUploadComplete}
          onClose={() => setShowBulkUpload(false)}
          validateAndFetchData={validateAndFetchData}
        />
      )}

      {isProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-gray-700">Processing items...</span>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsumptionSectionWidget;
